import React, { useState } from "react";
import useCustomizationContext from "../context/useCustomizationContext";
import { Alert, ButtonVariant, Dialog, TextFieldVariant } from "@merge-api/merge-javascript-shared";
import { publishWhiteLabelGuideChanges } from "../../../../../api-client/APIClient";
import { WhiteLabelGuideType } from "../context/CustomizationContext";
import { TextField } from "@merge-api/merge-javascript-shared";
import { showErrorToast } from "../../../../shared/Toasts";

const IS_ALPHANUMERIC_REGEX = /^[a-zA-Z0-9]*$/;

type BrandingWhiteLabelGuideModalProps = {
  whiteLabelGuide: WhiteLabelGuideType;
  handleWhiteLabelGuideChanges: (whiteLabelGuide: WhiteLabelGuideType) => void;
  handleHasSavedWhiteLabelGuide: (handleHasSavedWhiteLabelGuide: boolean) => void;
  isModalOpen: boolean;
  onHide: () => void;
};

const BrandingWhiteLabelGuideModal = ({
  whiteLabelGuide,
  handleWhiteLabelGuideChanges,
  handleHasSavedWhiteLabelGuide,
  isModalOpen,
  onHide,
}: BrandingWhiteLabelGuideModalProps) => {
  // STATE
  const [error, setError] = useState<boolean>(false);
  const { refreshCustomizationData } = useCustomizationContext();

  // HANDLERS
  const handleSubdomain = (subdomain: string) => {
    setError(!IS_ALPHANUMERIC_REGEX.test(subdomain));
    handleWhiteLabelGuideChanges({
      subdomain,
      square_logo: whiteLabelGuide?.square_logo ?? null,
      active: true,
    });
  };

  return (
    <Dialog
      open={isModalOpen}
      variant="md"
      title="Create white label guide"
      primaryButtonText="Set up"
      primaryButtonVariant={ButtonVariant.PrimaryGray}
      onClose={onHide}
      onPrimaryButtonClick={() => {
        // Close the modal immediately for better UX
        onHide();
        // Call API to publish changes and wait for completion
        publishWhiteLabelGuideChanges(whiteLabelGuide, handleHasSavedWhiteLabelGuide)
          .then(() => {
            // After the API call completes, refresh the customization data
            // This ensures the UI detects that a customization object has been created
            // which allows editing of the other customization fields
            refreshCustomizationData();
          })
          .catch(() => {
            showErrorToast("Failed to save white-label guides.");
          });
      }}
      primaryButtonDisabled={error}
    >
      <div className="flex flex-col gap-4">
        <>
          Automatically point URLs shown to your user during authentication in Merge Link to
          white-labeled version with your logo and preferred subdomain. Any customized links saved
          in Configuration will still be used.
        </>
        <div className="font-semibold">Preferred subdomain</div>
        <TextField
          variant={TextFieldVariant.Shadowed}
          placeholder="your-subdomain"
          error={error}
          errorText="Only alphanumeric characters are allowed"
          prefix="https://"
          prefixVariant="url"
          postfix={
            <div className="flex bg-gray-0 text-black rounded-br-md rounded-tr-md h-full items-center pl-2 pr-2">
              .integrations.guide
            </div>
          }
          onChange={(e) => handleSubdomain(e.target.value)}
        />
        <Alert showWarningIcon color="yellow" size="sm">
          Subdomain can take up to 24 hours to create and cannot be changed once set up
        </Alert>
      </div>
    </Dialog>
  );
};

export default BrandingWhiteLabelGuideModal;
